import { useEffect, useRef, useState } from 'react'

import {
  ActionIcon,
  Collapse,
  TextInput as MantineTextInput,
  Tooltip,
} from '@mantine/core'
import { Check, X, Upload } from 'lucide-react'

import { useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import DNButton from 'src/components/Buttons/Button/DNButton'
import PhoneInput from 'src/components/Inputs/PhoneInput/PhoneInput'
import StyledReadOnlyInput from 'src/components/Inputs/StyledReadOnlyInput/StyledReadOnlyInput'
import { toast } from 'src/components/Overlays/Toast/Toast'
import CallerIdStatusIcon from 'src/components/VerifiedCallerId/CallerIdStatusIcon'
import {
  GET_COMPANY,
  UPDATE_COMPANY,
  CREATE_COMPANY_LOGO_UPLOAD_URL,
} from 'src/graphql/companies.graphql'
import { VerifiedCallerIdOwnerType } from 'src/graphql/types/verifiedCallerIds'
import IconCheckmark from 'src/icons/IconCheckmark'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconCopy from 'src/icons/IconCopy'
import IconEdit from 'src/icons/IconEdit'
import IconInfoCircle from 'src/icons/IconInfoCircle'
import { isAdminOrOps } from 'src/lib/auth/permissions'
import {
  formatInternationalPhoneNumber,
  removeCountryCode,
  unformatPhoneNumber,
} from 'src/lib/phone.utils'
import PlacesAutocomplete from 'src/pages/ApplicantDetailsPage/tabs/ApplicationTab/components/AddressInput/PlacesAutocomplete'

import FieldLabel from './FieldLabel'

const SLUG_TOOLTIP =
  "The company's public application link (app.getdoublenickel.com/c/your-slug). Use lowercase letters, numbers, and hyphens only — no spaces or symbols. Hyphens can't lead, trail, or repeat. Example: acme-trucking"

const SLUG_PURPOSE_TOOLTIP =
  'Applicants log in to their application through this link (app.getdoublenickel.com/c/your-slug). Share it or copy it with the icon.'

interface GeneralInformationCardProps {
  companyData: {
    companyId: string
    companyName: string
    einNumber: string
    dotNumber: string
    companySlug?: string
    companyPhoneNumber: string
    companyFaxNumber: string
    address: {
      addressId: string
      street: string
      city: string
      state: string
      country: string
      zipCode: string
    }
  }
  initialOpened?: boolean
}

const GeneralInformationCard = ({
  companyData,
  initialOpened = true,
}: GeneralInformationCardProps) => {
  const { currentUser } = useAuth()
  const isAdmin = isAdminOrOps(currentUser)

  const [values, setValues] = useState({ ...companyData })
  const [editing, setEditing] = useState(false)
  const [opened, setOpened] = useState(initialOpened)
  const [copySuccess, setCopySuccess] = useState(false)
  const [slugCopied, setSlugCopied] = useState(false)
  const [logoError, setLogoError] = useState(false)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const [logoKey, setLogoKey] = useState(Date.now()) // Used to force logo refresh
  const fileInputRef = useRef<HTMLInputElement>(null)

  const logoUrl = `${process.env.AWS_PUBLIC_CDN_DOMAIN}/company/${companyData.companyId}/logo.png`

  const [updateCompany, { loading }] = useMutation(UPDATE_COMPANY, {
    onCompleted: () => {
      toast('Company information updated successfully', 'success')
      setEditing(false)
    },
    onError: (error) => {
      toast(`Failed to update company: ${error.message}`, 'error')
    },
    refetchQueries: [
      {
        query: GET_COMPANY,
        variables: { id: values.companyId },
      },
    ],
    awaitRefetchQueries: true,
  })

  const [createLogoUploadUrl] = useMutation(CREATE_COMPANY_LOGO_UPLOAD_URL)

  const handleLogoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const file = files[0]

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg']
    if (!allowedTypes.includes(file.type)) {
      toast('Please upload a PNG or JPEG image', 'error')
      return
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast('File size must be less than 5MB', 'error')
      return
    }

    setIsUploadingLogo(true)

    try {
      const { data, errors } = await createLogoUploadUrl({
        variables: {
          companyId: companyData.companyId,
          contentType: file.type,
        },
      })

      if (errors || !data?.createCompanyLogoUploadUrl) {
        throw new Error(errors?.[0]?.message || 'Failed to get upload URL')
      }

      const { uploadUrl } = data.createCompanyLogoUploadUrl

      // Upload the file directly to S3
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      })

      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`)
      }

      toast('Company logo uploaded successfully', 'success')
      setLogoError(false)
      // Force logo refresh by updating the key
      setLogoKey(Date.now())
    } catch (error) {
      console.error('Logo upload failed:', error)
      toast('Failed to upload logo. Please try again.', 'error')
    } finally {
      setIsUploadingLogo(false)
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  // Sync internal state with parent data changes only when not editing
  // This prevents the flash of old values when saving
  useEffect(() => {
    if (!editing) {
      setValues({ ...companyData })
    }
  }, [companyData, editing])

  const handleEdit = () => {
    setEditing(true)
  }

  const handleCancel = () => {
    setValues({ ...companyData })
    setEditing(false)
  }

  // Validation functions
  const validators = {
    companyName: (name) => {
      if (!name) return 'Company name is required'
      if (name.trim().length <= 2) {
        return 'Company name must be greater than 2 characters'
      }
      return undefined
    },
    ein: (ein) => {
      if (!ein) return 'EIN number is required'
      if (ein.trim().length <= 2) {
        return 'EIN number must be greater than 2 characters'
      }
      return undefined
    },
    dot: (dot) => {
      if (!dot) return 'DOT number is required'
      if (dot.trim().length <= 2) {
        return 'DOT number must be greater than 2 characters'
      }
      return undefined
    },
    phone: (phone) => {
      if (!phone) return undefined
      const phoneLength = unformatPhoneNumber(removeCountryCode(phone)).length
      if (phoneLength !== 10) {
        return 'Number must be 10 digits'
      }
      return undefined
    },
    fax: (fax) => {
      if (!fax) return undefined
      const faxLength = unformatPhoneNumber(removeCountryCode(fax)).length
      if (faxLength !== 10) {
        return 'Number must be 10 digits'
      }
      return undefined
    },
    address: (address) => {
      if (!address) return undefined
      const { street, city, state, country, zipCode } = address
      if (!street || !city || !state || !country || !zipCode) {
        return 'Please select a valid address from the dropdown'
      }
      return undefined
    },
    slug: (slug) => {
      if (!slug) return undefined
      const normalized = slug.trim().toLowerCase()
      if (normalized.length > 50) {
        return 'Slug must be 50 characters or fewer'
      }
      if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalized)) {
        return 'Use lowercase letters, numbers, and hyphens only'
      }
      return undefined
    },
  }

  const handleSave = async () => {
    const { companyId, address, ...rest } = values

    // Remove addressId and __typename from address before saving
    const addressWithoutId = { ...address }
    if ('addressId' in addressWithoutId) {
      delete addressWithoutId.addressId
    }
    if ('__typename' in addressWithoutId) {
      delete addressWithoutId.__typename
    }

    await updateCompany({
      variables: {
        id: companyId,
        input: {
          ...rest,
          companyPhoneNumber: rest.companyPhoneNumber
            ? unformatPhoneNumber(rest.companyPhoneNumber)
            : rest.companyPhoneNumber,
          companyFaxNumber: rest.companyFaxNumber
            ? unformatPhoneNumber(rest.companyFaxNumber)
            : rest.companyFaxNumber,
          address: addressWithoutId,
        },
      },
    })
  }

  const handleAddressChange = (addressData) => {
    setValues((prev) => ({
      ...prev,
      address: {
        addressId: addressData.addressId || '',
        street: addressData.street || '',
        city: addressData.city || '',
        state: addressData.state || '',
        country: addressData.country || 'USA',
        zipCode: addressData.zipCode || '',
      },
    }))
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
    }
  }

  const buildSlugLink = (slug: string) => {
    const base = process.env.DRIVER_APP_DOMAIN || window.location.origin
    return `${base.replace(/\/$/, '')}/c/${slug}`
  }

  const copySlugLink = async () => {
    if (!values.companySlug) return
    try {
      await navigator.clipboard.writeText(buildSlugLink(values.companySlug))
      setSlugCopied(true)
      setTimeout(() => setSlugCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy slug link:', error)
    }
  }

  // Validation checks
  const hasCompanyNameError =
    validators.companyName(values.companyName) !== undefined
  const hasEINError = validators.ein(values.einNumber) !== undefined
  const hasDOTError = validators.dot(values.dotNumber) !== undefined
  const hasPhoneError =
    validators.phone(values.companyPhoneNumber) !== undefined
  const hasFaxError = validators.fax(values.companyFaxNumber) !== undefined
  const hasAddressError = validators.address(values.address) !== undefined
  const hasSlugError =
    isAdmin && validators.slug(values.companySlug) !== undefined

  const isSaveDisabled =
    hasCompanyNameError ||
    hasEINError ||
    hasDOTError ||
    hasPhoneError ||
    hasFaxError ||
    hasAddressError ||
    hasSlugError ||
    loading

  const formatAddress = () => {
    const { street, city, state, zipCode } = values.address
    if (!street && !city && !state && !zipCode) return '-'
    return [street, city, state, zipCode].filter(Boolean).join(', ')
  }

  return (
    <div
      className={`flex w-full flex-col gap-2 rounded-lg border border-doubleNickel-gray-200 p-3`}
    >
      <div
        className="flex w-full cursor-pointer flex-row items-center gap-2 text-sm font-semibold text-doubleNickel-gray-900"
        onClick={() => setOpened((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpened((prev) => !prev)
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={opened}
      >
        <ActionIcon variant="subtle" className="pointer-events-none">
          <IconChevronDown
            className={`h-4 w-4 ${
              !opened && '-rotate-90'
            } fill-none stroke-doubleNickel-gray-500 transition-all`}
          />
        </ActionIcon>
        General
        {!editing && (
          <DNButton
            variant="transparent"
            onClick={(e) => {
              e.stopPropagation()
              if (!opened) setOpened(true)
              handleEdit()
            }}
            className="ml-auto text-doubleNickel-gray-700"
          >
            <IconEdit className="h-3.5 w-3.5 fill-none stroke-current" />
            Edit
          </DNButton>
        )}
        {editing && (
          <div className="ml-auto flex items-center gap-2">
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleSave()
              }}
              disabled={isSaveDisabled}
              className="text-doubleNickel-success-500"
            >
              <Check size={14} />
              Save
            </DNButton>
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleCancel()
              }}
              disabled={loading}
              className="text-doubleNickel-error-500"
            >
              <X size={14} />
              Cancel
            </DNButton>
          </div>
        )}
      </div>
      <Collapse in={opened} transitionDuration={400}>
        <div className="flex flex-col gap-4">
          <div className={`grid grid-cols-12 gap-3`}>
            {/* Row 1: Company ID, Company Name, DOT Number, EIN Number */}
            <div className="col-span-3">
              <div className="flex flex-col gap-2 pt-1">
                <div className="flex flex-row items-center gap-1 text-sm font-medium text-doubleNickel-gray-700">
                  <span>Company ID</span>
                  <Tooltip label="Unique identifier for your company">
                    <div className="flex items-center">
                      <IconInfoCircle className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-500" />
                    </div>
                  </Tooltip>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={() => copyToClipboard(values.companyId)}
                    title="Copy Company ID"
                  >
                    {copySuccess ? (
                      <IconCheckmark className="h-3 w-3 fill-none stroke-green-600" />
                    ) : (
                      <IconCopy className="h-3 w-3 fill-none stroke-doubleNickel-gray-500" />
                    )}
                  </ActionIcon>
                </div>
                <div className="truncate text-sm text-doubleNickel-gray-900">
                  {values.companyId || '-'}
                </div>
              </div>
            </div>

            <div className="col-span-3">
              {editing ? (
                <MantineTextInput
                  label={
                    <FieldLabel
                      label="Company Name"
                      tooltip="The legal name of your company"
                      required
                    />
                  }
                  value={values.companyName}
                  onChange={(e) =>
                    setValues({ ...values, companyName: e.target.value })
                  }
                  placeholder="Enter company name"
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                  error={validators.companyName(values.companyName)}
                />
              ) : (
                <StyledReadOnlyInput
                  label={
                    <FieldLabel
                      label="Company Name"
                      tooltip="The legal name of your company"
                    />
                  }
                  value={values.companyName || '-'}
                />
              )}
            </div>

            <div className="col-span-3">
              {editing ? (
                <MantineTextInput
                  label={
                    <FieldLabel
                      label="DOT Number"
                      tooltip="Department of Transportation number"
                      required
                    />
                  }
                  value={values.dotNumber}
                  onChange={(e) =>
                    setValues({ ...values, dotNumber: e.target.value })
                  }
                  placeholder="Enter DOT number"
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                  error={validators.dot(values.dotNumber)}
                />
              ) : (
                <StyledReadOnlyInput
                  label={
                    <FieldLabel
                      label="DOT Number"
                      tooltip="Department of Transportation number"
                    />
                  }
                  value={values.dotNumber || '-'}
                />
              )}
            </div>

            <div className="col-span-3">
              {editing ? (
                <MantineTextInput
                  label={
                    <FieldLabel
                      label="EIN Number"
                      tooltip="Employer Identification Number issued by the IRS"
                      required
                    />
                  }
                  value={values.einNumber}
                  onChange={(e) =>
                    setValues({ ...values, einNumber: e.target.value })
                  }
                  placeholder="Enter EIN number"
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                  error={validators.ein(values.einNumber)}
                />
              ) : (
                <StyledReadOnlyInput
                  label={
                    <FieldLabel
                      label="EIN Number"
                      tooltip="Employer Identification Number issued by the IRS"
                    />
                  }
                  value={values.einNumber || '-'}
                />
              )}
            </div>

            {/* Row 2: Address, Phone Number, Fax Number, Logo */}
            <div className="col-span-3">
              {editing ? (
                <PlacesAutocomplete
                  label={
                    <FieldLabel
                      label="Address"
                      tooltip="Primary business address for your company"
                    />
                  }
                  key={values.address.addressId}
                  value={values.address}
                  handleChange={handleAddressChange}
                  placeholder="Start typing an address..."
                  error={validators.address(values.address)}
                />
              ) : (
                <StyledReadOnlyInput
                  label={
                    <FieldLabel
                      label="Address"
                      tooltip="Primary business address for your company"
                    />
                  }
                  value={formatAddress()}
                />
              )}
            </div>

            <div className="col-span-3">
              {editing ? (
                <PhoneInput
                  label={
                    <FieldLabel
                      label="Phone Number"
                      tooltip="Main contact phone number for your company"
                    />
                  }
                  value={values.companyPhoneNumber}
                  onChange={(value) =>
                    setValues({ ...values, companyPhoneNumber: value })
                  }
                  placeholder="Enter phone number"
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                  error={validators.phone(values.companyPhoneNumber)}
                />
              ) : (
                <StyledReadOnlyInput
                  label={
                    <FieldLabel
                      label="Phone Number"
                      tooltip="Main contact phone number for your company"
                    />
                  }
                  value={
                    <div className="flex flex-row items-center gap-2">
                      {formatInternationalPhoneNumber(
                        values.companyPhoneNumber
                      ) || '-'}
                      {values.companyPhoneNumber && (
                        <CallerIdStatusIcon
                          ownerType={VerifiedCallerIdOwnerType.COMPANY}
                          phoneNumber={values.companyPhoneNumber}
                          caller="your team"
                          size={14}
                          // Recruiters may edit the company phone, so they may
                          // verify it too; only no-call adoption is admin-only.
                          canManage
                        />
                      )}
                    </div>
                  }
                />
              )}
            </div>

            <div className="col-span-3">
              {editing ? (
                <PhoneInput
                  label={
                    <FieldLabel
                      label="Fax Number"
                      tooltip="Fax number for your company"
                    />
                  }
                  value={values.companyFaxNumber}
                  onChange={(value) =>
                    setValues({ ...values, companyFaxNumber: value })
                  }
                  placeholder="Enter fax number"
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                  error={validators.fax(values.companyFaxNumber)}
                />
              ) : (
                <StyledReadOnlyInput
                  label={
                    <FieldLabel
                      label="Fax Number"
                      tooltip="Fax number for your company"
                    />
                  }
                  value={
                    formatInternationalPhoneNumber(values.companyFaxNumber) ||
                    '-'
                  }
                />
              )}
            </div>

            {/* Company Logo - Admin and OPS */}
            {isAdmin && (
              <div className="col-span-3">
                <div className="flex flex-col gap-1">
                  <div className="text-sm font-medium text-doubleNickel-gray-700">
                    <FieldLabel
                      label="Company Logo"
                      tooltip="Logo displayed on public forms and documents"
                    />
                  </div>
                  {editing ? (
                    <div className="flex items-center gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploadingLogo}
                        className="flex items-center gap-1.5 rounded border border-doubleNickel-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-doubleNickel-gray-700 transition-colors hover:bg-doubleNickel-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Upload size={12} />
                        {isUploadingLogo ? 'Uploading...' : 'Upload logo'}
                      </button>
                      {!logoError && (
                        <img
                          key={logoKey}
                          src={`${logoUrl}?t=${logoKey}`}
                          alt="Logo"
                          className="h-6 max-w-16 object-contain"
                          onError={() => setLogoError(true)}
                          onLoad={() => setLogoError(false)}
                        />
                      )}
                    </div>
                  ) : (
                    <div className="flex h-8 items-center justify-start">
                      {logoError ? (
                        <span className="text-xs text-doubleNickel-gray-400">
                          No logo
                        </span>
                      ) : (
                        <img
                          key={logoKey}
                          src={`${logoUrl}?t=${logoKey}`}
                          alt="Logo"
                          className="h-8 max-w-24 object-contain"
                          onError={() => setLogoError(true)}
                          onLoad={() => setLogoError(false)}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Company Slug — visible to everyone; only admin/ops can edit */}
            <div className="col-span-3">
              {isAdmin && editing ? (
                <MantineTextInput
                  label={
                    <FieldLabel
                      label="Company Slug"
                      tooltip={SLUG_TOOLTIP}
                      adminOnly
                    />
                  }
                  value={values.companySlug || ''}
                  onChange={(e) =>
                    setValues({ ...values, companySlug: e.target.value })
                  }
                  placeholder="your-company"
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                  error={validators.slug(values.companySlug)}
                />
              ) : (
                <div className="flex flex-col gap-2 pt-1">
                  <div className="flex flex-row items-center gap-1 text-sm font-medium text-doubleNickel-gray-700">
                    <span>Company Slug</span>
                    <Tooltip
                      label={SLUG_PURPOSE_TOOLTIP}
                      multiline
                      maw={300}
                      withArrow
                    >
                      <div className="flex items-center">
                        <IconInfoCircle className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-500" />
                      </div>
                    </Tooltip>
                    {values.companySlug && (
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        onClick={copySlugLink}
                        title="Copy application link"
                      >
                        {slugCopied ? (
                          <IconCheckmark className="h-3 w-3 fill-none stroke-green-600" />
                        ) : (
                          <IconCopy className="h-3 w-3 fill-none stroke-doubleNickel-gray-500" />
                        )}
                      </ActionIcon>
                    )}
                  </div>
                  <div className="truncate text-sm text-doubleNickel-gray-900">
                    {values.companySlug || '-'}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  )
}

export default GeneralInformationCard
